import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import PublicLayout from '../components/PublicLayout';
import { 
  Brain, FileCheck, Users, Shield, Zap, ArrowRight, 
  CheckCircle, FileText, Clock, Building2 
} from 'lucide-react';

const LandingPage = () => {
  const features = [
    {
      icon: Brain,
      title: 'Αυτόματος Έλεγχος',
      description: 'Αυτόματος έλεγχος όλων των εγγράφων. Εντοπισμός λαθών πριν την υποβολή.',
      color: 'cyan'
    },
    {
      icon: FileCheck,
      title: 'Διαχείριση Εγγράφων',
      description: 'Κεντρική αποθήκευση και οργάνωση όλων των δικαιολογητικών με αυτόματη κατηγοριοποίηση.',
      color: 'emerald'
    },
    {
      icon: Users,
      title: 'Συνεργασία Ομάδας',
      description: 'Προσκαλέστε και οργανώστε συνεργάτες, προμηθευτές, λογιστές και πελάτες σε μια πλατφόρμα.',
      color: 'violet'
    },
    {
      icon: Shield,
      title: 'Ασφάλεια Δεδομένων',
      description: 'Τα δεδομένα σας προστατεύονται με κρυπτογράφηση. Πλήρης συμμόρφωση με GDPR.',
      color: 'amber'
    },
    {
      icon: Clock,
      title: 'Αυτόματες Υπενθυμίσεις',
      description: 'Ειδοποιήσεις για εκκρεμή δικαιολογητικά και προθεσμίες. Ποτέ ξανά καθυστέρηση.',
      color: 'rose'
    },
    {
      icon: Zap,
      title: 'Γρήγορη Υλοποίηση',
      description: 'Από την αίτηση στην υλοποίηση, διαχειριστείτε όλο τον κύκλο ζωής του έργου.',
      color: 'blue'
    }
  ];

  const programs = [
    { name: 'ΕΣΠΑ', description: 'Προγράμματα Ευρωπαϊκής Ένωσης' },
    { name: 'ΟΑΕΔ', description: 'Προγράμματα Απασχόλησης' },
    { name: 'Αναπτυξιακός Νόμος', description: 'Επενδυτικά Κίνητρα' },
  ];

  const stats = [
    { value: '50%', label: 'Εξοικονόμηση Χρόνου' },
    { value: '0', label: 'Χαμένα Έγγραφα' },
    { value: '24/7', label: 'Πρόσβαση' },
  ];

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 -left-40 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Διαχείριση Αίτησης &
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600">
                  Παρακολούθησης Επενδυτικών Σχεδίων
                </span>
                με αυτόματο έλεγχο δικαιολογητικών
              </h1>

              <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-lg">
                Η πλατφόρμα που απλοποιεί τη διαχείριση αιτήσεων ΕΣΠΑ, ΟΑΕΔ και Αναπτυξιακού Νόμου. 
                Αυτόματος έλεγχος δικαιολογητικών.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/login">
                  <Button className="w-full sm:w-auto rounded-full px-8 py-6 text-lg bg-slate-900 hover:bg-slate-800 text-white font-medium hover:scale-105 transition-all">
                    Ξεκινήστε Δωρεάν
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="outline" className="w-full sm:w-auto rounded-full px-8 py-6 text-lg font-medium border-slate-300 hover:bg-slate-50">
                    Δείτε τα Πακέτα
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="flex gap-12 pt-8 border-t border-slate-200">
                {stats.map((stat, index) => (
                  <div key={index}>
                    <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {stat.value}
                    </div>
                    <div className="text-sm text-slate-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Content - Visual */}
            <div className="relative">
              <div className="relative bg-white rounded-3xl shadow-2xl border border-slate-200 p-8 overflow-hidden">
                {/* Decorative gradient */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 blur-3xl" />
                
                {/* Mock UI */}
                <div className="relative space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">Αυτόματος Έλεγχος</div>
                      <div className="text-sm text-slate-500">Έλεγχος σε εξέλιξη...</div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {['Τιμολόγιο αγοράς.pdf', 'Δήλωση ΜΜΕ.pdf', 'Εξουσιοδότηση.pdf'].map((doc, i) => (
                      <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-700">{doc}</span>
                        </div>
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      </div>
                    ))}
                  </div>

                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Όλα τα έγγραφα είναι σωστά!</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Όλα όσα χρειάζεστε σε μία πλατφόρμα
            </h2>
            <p className="text-lg text-slate-600">
              Εργαλεία σχεδιασμένα για συμβούλους επιχειρήσεων, λογιστές και επιχειρήσεις 
              που θέλουν να απλοποιήσουν τη διαχείριση προγραμμάτων χρηματοδότησης.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group p-8 rounded-2xl border border-slate-200 bg-white hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`w-14 h-14 rounded-2xl bg-${feature.color}-100 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <feature.icon className={`w-7 h-7 text-${feature.color}-600`} />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs" className="py-24 md:py-32 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Υποστήριξη για όλα τα προγράμματα
            </h2>
            <p className="text-lg text-slate-400">
              Διαχειριστείτε αιτήσεις από όλα τα μεγάλα προγράμματα χρηματοδότησης στην Ελλάδα.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <div
                key={index}
                className="group relative p-8 rounded-2xl bg-slate-800/50 border border-slate-700 hover:border-cyan-500/50 transition-all duration-300"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative">
                  <Building2 className="w-12 h-12 text-cyan-400 mb-6" />
                  <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {program.name}
                  </h3>
                  <p className="text-slate-400">
                    {program.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Έτοιμοι να ξεκινήσετε;
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Δοκιμάστε το ESPA Manager δωρεάν για 10 ημέρες. 
            Χωρίς δέσμευση, χωρίς πιστωτική κάρτα.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button className="rounded-full px-8 py-6 text-lg bg-slate-900 hover:bg-slate-800 text-white font-medium hover:scale-105 transition-all">
                Δημιουργία Λογαριασμού
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="rounded-full px-8 py-6 text-lg font-medium border-slate-300 hover:bg-slate-50">
                Επικοινωνήστε μαζί μας
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default LandingPage;
